import { useLocation, useSearchParams } from 'react-router-dom';

/**
 * The context a lab screen hands to the next one, so Samples → Result entry →
 * Validate & release → Reports never makes anybody find the same encounter
 * twice.
 *
 * Carried as query parameters (shareable, survives a refresh):
 *   venue      partner id of the selected venue (company-bound accounts pick
 *              one; a venue account has its own and ignores this)
 *   subject    encounter id, as Result entry and Validate & release name it
 *   encounter  encounter id, as Reports names it (same value, its own key)
 *   sample     specimen id
 *   report     lab report id
 *   rev        that report's revision
 *   back       where "Back" returns to: a relative path WITH its query string,
 *              so the queue reopens with its filters, page and selection
 *
 * Only ids travel, never names: subjects stay pseudonymous at the bench.
 */
export type LabContext = Partial<Record<'venue' | 'subject' | 'encounter' | 'sample' | 'report' | 'rev', string | number | null>> & {
  /** Pass `true` to return to the current page (path + query). */
  back?: string | true | null;
};

/** The screens, by name, under whichever console prefix is current (/lab or /partner). */
export type LabScreen = 'samples' | 'results' | 'release' | 'reports';
const SEGMENT: Record<LabScreen, Record<string, string>> = {
  samples: { '/lab': 'worklist', '/partner': 'samples' },
  results: { '/lab': 'results', '/partner': 'results' },
  release: { '/lab': 'release', '/partner': 'release' },
  reports: { '/lab': 'reports', '/partner': 'reports' },
};

/** `/lab` or `/partner`, from a pathname. */
export function consolePrefix(pathname: string): '/lab' | '/partner' {
  return pathname.startsWith('/partner') ? '/partner' : '/lab';
}

/** A link to another lab screen carrying the given context. */
export function labHref(pathname: string, screen: LabScreen, ctx: LabContext = {}, here?: string): string {
  const prefix = consolePrefix(pathname);
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(ctx)) {
    if (k === 'back' || v == null || v === '') continue;
    q.set(k, String(v));
  }
  if (ctx.back) q.set('back', ctx.back === true ? (here ?? pathname) : ctx.back);
  const s = q.toString();
  return `${prefix}/${SEGMENT[screen][prefix]}${s ? `?${s}` : ''}`;
}

/**
 * Builds context links from the current page: `to('release', { subject, back: true })`
 * returns to exactly this page — path, filters, page and selection.
 */
export function useLabLinks() {
  const { pathname, search } = useLocation();
  const here = `${pathname}${search}`;
  return { to: (screen: LabScreen, ctx: LabContext = {}) => labHref(pathname, screen, ctx, here), here };
}

const LABEL: Array<[RegExp, string]> = [
  [/\/(worklist|samples)(\?|$)/, 'Back to Samples'],
  [/\/results(\?|$)/, 'Back to Result entry'],
  [/\/release(\?|$)/, 'Back to Validate & release'],
  [/\/reports(\?|$)/, 'Back to Reports'],
];

/**
 * The explicit Back action, from `?back=`. Only a same-app relative path is
 * honoured (never an absolute or protocol-relative URL), so a crafted link
 * cannot send anybody off the site. Null when there is nowhere to go back to.
 */
export function useBackLink(): { to: string; label: string } | null {
  const [params] = useSearchParams();
  const back = params.get('back');
  if (!back || !back.startsWith('/') || back.startsWith('//')) return null;
  const label = LABEL.find(([re]) => re.test(back))?.[1] ?? 'Back';
  return { to: back, label };
}

/** The value of one context parameter, or '' — for seeding a screen's own state. */
export function useLabParam(key: keyof LabContext): string {
  const [params] = useSearchParams();
  return params.get(key) ?? '';
}
